// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import TextField from "@insite/mobius/TextField";
import PasswordRequirementValidator from "@rpsc/App/Components/Common/PasswordRequirements";
import UpdateBasicInfoForm from "@rpsc/App/Handlers/CreateAccount/UpdateBasicInfoForm";
import { xValidateEmail } from "@rpsc/App/Services/EmailValidation";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import Tooltip, { TooltipPresentationProps } from "@rpsc/MobiusOverrides/Tooltip/Tooltip";
import { FORM_IDS, placeHolders } from "@rpsc/XDL/constants";
import { xValidateOnSubmit } from "@rpsc/XDL/Utilities";
import React, { Dispatch, SetStateAction, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

type OwnProps = {
    onSubmit: () => void;
    submitBtnLabel?: string;
    setErrorMessage?: Dispatch<SetStateAction<string>>;
};

const mapStateToProps = (state: CustomAppState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    formData: state.xCreateAccount.accountInfo.basicInfo,
});

const mapDispatchToProps = {
    UpdateBasicInfoForm,
    validatePassword,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CreateAccountBasicInfoFormStyles {
    container?: GridContainerProps;
    infoGridItem?: GridItemProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    toolTipStyles?: TooltipPresentationProps;
    submitButtonGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

export const createAccountBasicInfoFormStyles: CreateAccountBasicInfoFormStyles = {};

const styles = createAccountBasicInfoFormStyles;

const formId = FORM_IDS.BASIC_INFO;

const StyledForm = getStyledWrapper("form");

const CreateAccountBasicInfoForm = ({
    submitBtnLabel,
    accountSettings,
    formData,
    UpdateBasicInfoForm,
    validatePassword,
    onSubmit,
    setErrorMessage
}: Props) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isPasswordFieldFocused, setIsPasswordFieldFocused] = useState(false);
    const [passwordErrorMsg, setPasswordErrorMsg] = useState("");
    const [confirmPasswordErrorMsg, setConfirmPasswordErrorMsg] = useState("");

    const { firstName, lastName, email, userName, phone, password, confirmPassword } = formData;

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        useEmailAsUserName,
    } = accountSettings;

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMsg(errorMessage as string);
        return !errorMessage;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;
        UpdateBasicInfoForm({ [name]: value });
    };

    const passwordChangeHandler = (value: string) => {
        validateConfirmPassword(value, confirmPassword);
        validatePassword({
            password: value,
            onComplete: errorMessage => {
                setPasswordErrorMsg(errorMessage as string);
            },
        });
        UpdateBasicInfoForm({ password: value });
    };

    const confirmPasswordChangeHandler = (value: string) => {
        UpdateBasicInfoForm({ confirmPassword: value });
        validateConfirmPassword(password, value);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (xValidateOnSubmit(formId).length > 0 || passwordErrorMsg || confirmPasswordErrorMsg) {
            return;
        }
        // check for email
        const result = await xValidateEmail(email)
        if(!result.successful) {
            setErrorMessage?.(result.errorMessage!)
            return;
        }
        onSubmit();
    };

    return (
        <StyledForm onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.container}>
                <GridItem {...styles.infoGridItem}>
                    <TextField
                        label={translate("First Name")}
                        required
                        type="firstName"
                        placeholder={translate("First Name")}
                        value={firstName}
                        name="firstName"
                        onChange={handleInputChange}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <TextField
                        label={translate("Last Name")}
                        required
                        type="lastName"
                        placeholder={translate("Last Name")}
                        value={lastName}
                        name="lastName"
                        onChange={handleInputChange}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <TextField
                        label={translate("Email")}
                        required
                        type="email"
                        placeholder={placeHolders.email}
                        value={email}
                        name="email"
                        onChange={handleInputChange}
                        {...{ formId }}
                    />
                </GridItem>
                {!useEmailAsUserName && (
                    <GridItem {...styles.infoGridItem}>
                        <TextField
                            label={translate("UserName")}
                            required
                            type="userName"
                            placeholder={translate("You UserName")}
                            value={userName}
                            name="userName"
                            onChange={handleInputChange}
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                <GridItem {...styles.infoGridItem}>
                    <TextField
                        label={translate("Phone Number")}
                        required
                        type="phone"
                        placeholder={placeHolders.phone}
                        value={phone}
                        name="phone"
                        onChange={handleInputChange}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <Tooltip
                        {...styles.toolTipStyles}
                        placement="top"
                        offset={[0, -5]}
                        text={
                            <PasswordRequirementValidator
                                password={password}
                                passwordMinimumLength={passwordMinimumLength}
                                passwordRequiresDigit={passwordRequiresDigit}
                                passwordRequiresLowercase={passwordRequiresLowercase}
                                passwordRequiresUppercase={passwordRequiresUppercase}
                                passwordRequiresSpecialCharacter={passwordRequiresSpecialCharacter}
                            />
                        }
                        visible={isPasswordFieldFocused}
                        triggerComponent={
                            <TextField
                                label={translate("Password")}
                                value={password}
                                required
                                type={showPassword ? undefined : "password"}
                                iconProps={{ ...styles.passwordIcon, src: showPassword ? EyeOff : Eye }}
                                iconClickableProps={{
                                    onClick: () => {
                                        setShowPassword(!showPassword);
                                    },
                                    type: "button",
                                    "aria-label": "Show Password",
                                }}
                                data-test-selector="createAccount_password"
                                placeholder={translate("Password")}
                                onChange={e => passwordChangeHandler(e.currentTarget.value)}
                                error={!isPasswordFieldFocused && passwordErrorMsg}
                                onFocus={() => setIsPasswordFieldFocused(true)}
                                onBlur={() => setIsPasswordFieldFocused(false)}
                                {...{ formId }}
                            />
                        }
                    ></Tooltip>
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <TextField
                        type={showConfirmPassword ? undefined : "password"}
                        label={translate("Confirm Password")}
                        value={confirmPassword}
                        iconProps={{
                            ...styles.confirmPasswordIcon,
                            src: showConfirmPassword ? EyeOff : Eye,
                        }}
                        iconClickableProps={{
                            onClick: () => {
                                setShowConfirmPassword(!showConfirmPassword);
                            },
                            type: "button",
                            "aria-label": "Show Password",
                        }}
                        data-test-selector="createAccount_confirmPassword"
                        required
                        placeholder={translate("Confirm Your Password")}
                        onChange={e => confirmPasswordChangeHandler(e.currentTarget.value)}
                        error={confirmPasswordErrorMsg}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.submitButtonGridItem}>
                    <Button {...styles.submitButton} type="submit" aria-label={submitBtnLabel ?? translate("Next")}>
                        {submitBtnLabel ?? translate("Next")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateAccountBasicInfoForm);
